import moment from 'moment-timezone';
var DATE_LABEL_FORMAT = 'ddd, MMMM D';
/**
 * Formats a slot's start/end into the drawer label, e.g.
 * "Tue, June 8, 10:00 am - 10:30 am".
 *
 * The server already localizes the times to `input.timeZone`, so we parse with
 * `parseZone` to keep the offset it returned and never re-convert to the
 * browser's local zone (integration gotcha #5).
 */
export var formatSlotLabel = function (startTime, endTime, timeFormat) {
    var start = moment.parseZone(startTime);
    var end = moment.parseZone(endTime);
    return "".concat(start.format(DATE_LABEL_FORMAT), ", ").concat(start.format(timeFormat), " - ").concat(end.format(timeFormat));
};
var toConflictedUser = function (invitee) {
    var _a, _b;
    return ({
        id: (_a = invitee.userId) !== null && _a !== void 0 ? _a : invitee.email,
        name: (_b = invitee.name) !== null && _b !== void 0 ? _b : invitee.email,
    });
};
/**
 * Maps one API slot to the drawer's display shape. "Conflicts" are the invitees
 * with a free/busy conflict (`unavailableInvitees`) — i.e. who can't attend.
 * Outside-working-hours invitees are intentionally not treated as conflicts
 * (they're free, just off-hours) and surface via bylines instead.
 */
export var toTimeSuggestion = function (slot, timeFormat) { return ({
    id: slot.startTime,
    startTime: slot.startTime,
    endTime: slot.endTime,
    label: formatSlotLabel(slot.startTime, slot.endTime, timeFormat),
    conflicts: slot.attendance.unavailableInvitees.map(toConflictedUser),
}); };
/**
 * Short label for a quick-pick pill: day + start time only, e.g.
 * "Today, 10:30 am" or "Tue, 9:30 am". "Today" is compared in the slot's own
 * offset so it matches the event timezone, not the browser's.
 */
export var formatQuickPickLabel = function (startTime, timeFormat, todayLabel) {
    var slot = moment.parseZone(startTime);
    var nowInSlotOffset = moment().utcOffset(slot.utcOffset());
    var day = slot.isSame(nowInSlotOffset, 'day')
        ? todayLabel
        : slot.format('ddd');
    return "".concat(day, ", ").concat(slot.format(timeFormat));
};
/** Calendar-day key (in the slot's own offset) used to group by day. */
var dayKey = function (startTime) {
    return moment.parseZone(startTime).format('YYYY-MM-DD');
};
/**
 * Picks up to `count` suggestions, preferring distinct days (highest-ranked
 * slot per day first). If there aren't enough distinct days, fills the rest
 * with the next highest-ranked slots regardless of day.
 */
export var pickDistinctDaySuggestions = function (suggestions, count) {
    var picked = [];
    var pickedIds = new Set();
    var usedDays = new Set();
    suggestions.forEach(function (suggestion) {
        if (picked.length >= count) {
            return;
        }
        var day = dayKey(suggestion.startTime);
        if (!usedDays.has(day)) {
            usedDays.add(day);
            picked.push(suggestion);
            pickedIds.add(suggestion.id);
        }
    });
    // Fall back to same-day slots when distinct days don't fill the quota.
    if (picked.length < count) {
        suggestions.forEach(function (suggestion) {
            if (picked.length < count && !pickedIds.has(suggestion.id)) {
                picked.push(suggestion);
                pickedIds.add(suggestion.id);
            }
        });
    }
    return picked;
};
/**
 * Splits ranked slots into "everyone available" vs "some conflicts", preserving
 * the server's ranking within each group.
 */
export var groupSuggestions = function (slots, timeFormat) {
    var available = [];
    var conflicts = [];
    slots.forEach(function (slot) {
        var suggestion = toTimeSuggestion(slot, timeFormat);
        if (suggestion.conflicts.length > 0) {
            conflicts.push(suggestion);
        }
        else {
            available.push(suggestion);
        }
    });
    return { available: available, conflicts: conflicts };
};
